/**
 * PRD.330 — Clinic Collection Details report definition.
 *
 * One row = one `invoice_payments` payment event (D2). The definition says
 * WHAT the report is (columns, filter kinds, search, defaults, aggregates);
 * the SQL field mapping lives in @helios/data-access and display rendering in
 * web-admin, so the definition stays transport-agnostic (D30).
 */
import { PAYMENT_METHODS } from '../billing/payment-methods.js';
import type { ReportDefinition } from './report-types.js';

export type ClinicCollectionsColumnId =
  | 'paymentDate'
  | 'patient'
  | 'invoiceNo'
  | 'method'
  | 'amount'
  | 'notes';

export const CLINIC_COLLECTIONS_REPORT: ReportDefinition<ClinicCollectionsColumnId> = {
  id: 'clinic-collection-details',
  title: 'Clinic Collection Details',
  columns: [
    // Date filtering is the global Date From/To range (PRD.330 §11.5).
    {
      id: 'paymentDate',
      label: 'Date',
      valueType: 'date',
      sortable: true,
      filter: null,
      minWidth: 120,
    },
    {
      id: 'patient',
      label: 'Patient',
      valueType: 'text',
      sortable: true,
      filter: 'text',
      minWidth: 200,
    },
    {
      id: 'invoiceNo',
      label: 'Invoice #',
      valueType: 'text',
      sortable: true,
      filter: 'text',
      minWidth: 130,
    },
    {
      id: 'method',
      label: 'Payment Method',
      valueType: 'enum',
      sortable: true,
      filter: 'values',
      minWidth: 150,
    },
    {
      id: 'amount',
      label: 'Amount',
      valueType: 'money',
      sortable: true,
      filter: 'range',
      align: 'right',
      minWidth: 130,
    },
    {
      id: 'notes',
      label: 'Payment Notes',
      valueType: 'text',
      sortable: false,
      filter: 'text',
      minWidth: 240,
    },
  ],
  searchableColumns: ['patient', 'invoiceNo', 'notes'],
  facetColumns: ['method'],
  defaultSort: { columnId: 'paymentDate', direction: 'desc' },
  datePresets: ['today', 'yesterday', 'last7Days', 'thisMonth', 'lastMonth', 'ytd'],
  defaultDatePreset: 'today',
  aggregates: [
    { kind: 'count', key: 'totalCount' },
    { kind: 'sum', key: 'amountSum', columnId: 'amount' },
    // PRD.330 Phase 5 — Average Payment, derived from the full-query summary.
    {
      kind: 'ratio',
      key: 'averagePayment',
      numeratorKey: 'amountSum',
      denominatorKey: 'totalCount',
    },
  ],
  pageSize: 50,
  visualizations: [
    {
      id: 'collectionsByMethod',
      title: 'Collections by Payment Method',
      kind: 'categoryBar',
      dimension: 'method',
      measure: { kind: 'sum', columnId: 'amount', key: 'amountSum' },
      secondaryMeasure: { kind: 'count', key: 'paymentCount' },
      // Canonical order from the billing domain — not a second method list.
      knownCategories: PAYMENT_METHODS.map((m) => m.value),
    },
    {
      // PRD.330 Phase 5 — grouped by day/week/month from the applied range.
      id: 'collectionsOverTime',
      title: 'Collections Over Time',
      kind: 'timeSeries',
      dateDimension: 'paymentDate',
      measure: { kind: 'sum', columnId: 'amount', key: 'amountSum' },
      secondaryMeasure: { kind: 'count', key: 'paymentCount' },
    },
  ],
};
